import { ReactNode, useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';
import { BottomNav } from './BottomNav';
import { Header } from './Header';
import { WeatherPrayerBar } from './WeatherPrayerBar';
import { GlobalCategoryMenu } from './GlobalCategoryMenu';
import { LiveWidget } from './LiveWidget';

const Footer = lazy(() => import('./Footer').then(module => ({ default: module.Footer })));
const Sidebar = lazy(() => import('./Sidebar').then(module => ({ default: module.Sidebar })));

interface LayoutProps {
  children: ReactNode;
  hideNav?: boolean;
  hideHeader?: boolean;
}

export function Layout({ children, hideNav = false, hideHeader = false }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [headerMode, setHeaderMode] = useState<'full' | 'compact' | 'hidden'>('full');
  const lastScrollY = useRef(0);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    // Reset scroll on route change
    window.scrollTo(0, 0);
    // Also reset the scroll to top button visibility
    setShowScrollTop(false);
    setHeaderMode('full');
    lastScrollY.current = 0;
  }, [location.pathname]);

  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    setShowScrollTop(currentScrollY > 300);
    
    if (currentScrollY <= 50) {
      setHeaderMode('full');
    } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
      setHeaderMode('hidden'); // scrolling down
    } else if (currentScrollY < lastScrollY.current) {
      setHeaderMode('compact'); // scrolling up
    }
    
    lastScrollY.current = currentScrollY;
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 flex justify-center items-start">
      <div className={`w-full md:max-w-3xl lg:max-w-5xl xl:max-w-7xl bg-white dark:bg-gray-950 min-h-screen shadow-2xl flex flex-col ${!hideNav ? 'pt-0 pb-0' : ''}`}>
        {!hideHeader && (
          <div className={`sticky top-0 z-[100] flex flex-col shadow-sm print:hidden bg-white dark:bg-gray-950 h-max w-full transition-transform duration-300 ${headerMode === 'hidden' ? '-translate-y-full' : 'translate-y-0'}`}>
            <div className={`transition-all duration-300 overflow-hidden ${headerMode === 'full' ? 'max-h-[100px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <WeatherPrayerBar />
            </div>
            <Header onMenuClick={() => setIsSidebarOpen(true)} />
            <GlobalCategoryMenu />
            <LiveWidget />
          </div>
        )}
        <main ref={mainRef} className="flex-1 flex flex-col bg-white dark:bg-gray-950 relative z-0 min-h-[150vh]">
          <div className="flex-1 pb-4 flex flex-col">
            {children}
          </div>
          <div className="print:hidden">
            <Suspense fallback={<div className="h-[200px] bg-white dark:bg-gray-950"></div>}>
              <Footer />
            </Suspense>
          </div>
        </main>
        {!hideNav && <div className="print:hidden"><BottomNav /></div>}
        <Suspense fallback={null}>
          <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        </Suspense>
        
        {/* Scroll to Top Button (Hidden on Mobile) */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="hidden md:flex fixed bottom-[80px] lg:bottom-8 right-6 lg:right-8 z-50 bg-[#D32F2F] hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-all transform hover:scale-110 active:scale-95 items-center gap-1.5 group"
            aria-label="Scroll to top"
          >
            <ChevronUp size={20} />
            <span className="font-bengali font-bold text-sm hidden group-hover:block transition-all">উপরে</span>
          </button>
        )}
      </div>
    </div>
  );
}

